/**
 * Cuadratura de documentos de venta en CLP contra el precio público (bruto).
 *
 * Funciones puras, sin dependencias de Angular, para poder probarlas de forma
 * exhaustiva. Replican cómo SAP Business One calcula un documento:
 *   - precio unitario neto redondeado a los decimales que SAP conserva para la moneda,
 *   - total de línea = precio x cantidad x (1 - descuento), redondeado a peso,
 *   - cada impuesto redondeado a peso una vez por documento, sobre la suma de sus bases.
 * El residuo contra el total bruto esperado se cubre con el Redondeo oficial de SAP.
 */

/** Tope histórico del redondeo para líneas cuyo bruto fue inferido en documentos que no son boleta. */
export const TOPE_REDONDEO_INFERIDO = 5;

const MAX_DECIMALES_PRECIO = 6;

export function redondear(valor: number, decimales: number): number {
  const v = Number(valor) || 0;
  const p = Math.pow(10, decimales > 0 ? decimales : 0);
  const r = Math.round((Math.abs(v) + Number.EPSILON) * p) / p;
  return v < 0 ? -r : r;
}

/**
 * Decimales con los que SAP conserva un precio unitario en CLP.
 * Se toma el menor entre los decimales de precio de la sociedad (OADM.PriceDec) y los de la
 * moneda (OCRN.Decimals, -1 = usar el valor general). Ante cualquier dato inválido se asume 0:
 * subestimar solo agranda el redondeo, sobrestimar haría que SAP guarde otro total.
 */
export function decimalesPrecioEfectivos(priceDec: any, decimalesMoneda?: any): number {
  const aEntero = (x: any) => (x === null || x === undefined || x === '' ? NaN : Number(x));
  let dec = aEntero(priceDec);
  if (!Number.isInteger(dec) || dec < 0) {
    return 0;
  }
  dec = Math.min(dec, MAX_DECIMALES_PRECIO);
  const moneda = aEntero(decimalesMoneda);
  if (Number.isInteger(moneda) && moneda >= 0) {
    dec = Math.min(dec, moneda);
  }
  return dec;
}

export function netoDesdeBruto(bruto: number, tasaTotal: number, decimales: number): number {
  return redondear((Number(bruto) || 0) / (1 + (Number(tasaTotal) || 0) / 100), decimales);
}

export function brutoDesdeNeto(neto: number, tasaTotal: number): number {
  return Math.round((Number(neto) || 0) * (1 + (Number(tasaTotal) || 0) / 100));
}

/** Indica si un bruto guardado en la línea sigue correspondiendo a su precio neto actual. */
export function brutoEsConsistente(bruto: number, neto: number, tasaTotal: number, decimales: number): boolean {
  if (!(bruto > 0) || !(neto > 0)) {
    return false;
  }
  return netoDesdeBruto(bruto, tasaTotal, decimales) === redondear(neto, decimales)
    || brutoDesdeNeto(neto, tasaTotal) === Math.round(bruto);
}

export function precioTrasDescuento(neto: number, descuentoPct: number, decimales: number): number {
  const n = Number(neto) || 0;
  return redondear(n - (n * (Number(descuentoPct) || 0)) / 100, decimales);
}

/**
 * Total neto de una línea en pesos, como lo calcula SAP: precio x cantidad x (1 - descuento),
 * redondeando solo el resultado. El "precio tras descuento" que muestra SAP es informativo y
 * no interviene (verificado con cotizaciones de prueba el 08-10-2026: 3.584 x 108 con 10% = 348.365).
 */
export function totalLinea(neto: number, descuentoPct: number, cantidad: number): number {
  return redondeaPeso((Number(neto) || 0) * (Number(cantidad) || 0) * (100 - (Number(descuentoPct) || 0)) / 100);
}

/** Redondeo a peso; toFixed absorbe el ruido binario para que un ,5 exacto no quede en ,4999999. */
export function redondeaPeso(valor: number): number {
  return Math.round(parseFloat((Number(valor) || 0).toFixed(6)));
}

export interface BaseImpuesto {
  /** Total neto de la línea. */
  lineTotal: number;
  tasaPrimaria: number;
  tasaIvaExtra: number;
}

/**
 * Impuestos de un documento en pesos, como los calcula SAP: cada impuesto (ILA, IVA, ...) se
 * redondea UNA vez sobre la suma de las bases de todas las líneas que lo llevan, no línea por línea.
 * Verificado contra 3.715 grupos de impuesto de facturas reales (100% de coincidencia) y con
 * cotizaciones de prueba el 08-10-2026. Los impuestos se agrupan por su tasa.
 * "primario" e "ivaExtra" son los dos casilleros que muestra el panel de totales.
 */
export function impuestosDocumento(lineas: BaseImpuesto[]): { primario: number, ivaExtra: number, total: number } {
  const grupos: { [tasa: string]: { tasa: number, basePrimaria: number, baseExtra: number } } = {};
  const acumula = (tasa: number, base: number, esPrimaria: boolean) => {
    if (!(tasa > 0)) { return; }
    const g = grupos[tasa] || (grupos[tasa] = { tasa, basePrimaria: 0, baseExtra: 0 });
    if (esPrimaria) { g.basePrimaria += base; } else { g.baseExtra += base; }
  };
  (lineas || []).forEach(l => {
    const base = Number(l.lineTotal) || 0;
    acumula(Number(l.tasaPrimaria) || 0, base, true);
    acumula(Number(l.tasaIvaExtra) || 0, base, false);
  });

  let primario = 0, ivaExtra = 0;
  Object.keys(grupos).forEach(k => {
    const g = grupos[k];
    const impuesto = redondeaPeso(((g.basePrimaria + g.baseExtra) * g.tasa) / 100);
    // Si un mismo impuesto cae en ambos casilleros, el peso de ajuste queda en el de IVA
    const partePrimaria = g.baseExtra === 0 ? impuesto : redondeaPeso((g.basePrimaria * g.tasa) / 100);
    primario += partePrimaria;
    ivaExtra += impuesto - partePrimaria;
  });
  return { primario, ivaExtra, total: primario + ivaExtra };
}

export function brutoEsperadoLinea(bruto: number, descuentoPct: number, cantidad: number): number {
  return redondeaPeso((Number(bruto) || 0) * (Number(cantidad) || 0) * (100 - (Number(descuentoPct) || 0)) / 100);
}

/**
 * Máxima diferencia posible, en pesos, entre el bruto esperado de una línea y lo que suma
 * neto + impuestos, atribuible únicamente a redondeos:
 *   - el precio unitario pierde hasta media unidad del último decimal, y eso se multiplica
 *     por la cantidad y por el factor de impuestos;
 *   - total de línea, impuestos y bruto esperado se redondean a peso.
 */
export function toleranciaLinea(cantidad: number, tasaTotal: number, decimales: number): number {
  const factor = 1 + (Number(tasaTotal) || 0) / 100;
  const pasoPrecio = Math.pow(10, -(decimales > 0 ? decimales : 0));
  return Math.abs(Number(cantidad) || 0) * 0.5 * pasoPrecio * factor + 0.5 * factor + 1.5;
}

export interface LineaCuadratura {
  /** Precio neto unitario tal como se envía a SAP. */
  neto: number;
  cantidad: number;
  descuentoPct: number;
  tasaPrimaria: number;
  tasaIvaExtra: number;
  /** Precio público unitario (con impuestos); 0 o indefinido si la línea no tiene. */
  bruto?: number;
  /** La línea manda por su bruto: precio digitado por el vendedor o documento boleta. */
  guiadaPorBruto?: boolean;
  /** Total neto de la línea ya calculado en pantalla; si no viene se calcula desde el precio. */
  totalLineaReal?: number;
}

export interface ResultadoCuadratura {
  neto: number;
  impuestoPrimario: number;
  ivaExtra: number;
  /** Neto + impuestos, sin redondeo: lo que SAP calcula por sí solo. */
  totalCalculado: number;
  totalBrutoEsperado: number;
  tolerancia: number;
  aplicaRedondeo: boolean;
  redondeo: number;
  total: number;
}

/**
 * Totales de un documento CLP sin descuento de cabecera y decisión de Redondeo.
 * El redondeo se aplica solo cuando la diferencia cabe dentro de lo explicable por redondeos.
 */
export function cuadrarDocumento(lineas: LineaCuadratura[], decimales: number): ResultadoCuadratura {
  let neto = 0, esperado = 0, tolerancia = 0;
  let hayBruto = false, todasGuiadas = true;
  const bases: BaseImpuesto[] = [];
  const basesSinBruto: BaseImpuesto[] = [];

  (lineas || []).forEach(l => {
    const tasaTotal = (Number(l.tasaPrimaria) || 0) + (Number(l.tasaIvaExtra) || 0);
    const lt = (l.totalLineaReal !== undefined && l.totalLineaReal !== null)
      ? Number(l.totalLineaReal) || 0
      : totalLinea(l.neto, l.descuentoPct, l.cantidad);
    const base: BaseImpuesto = { lineTotal: lt, tasaPrimaria: l.tasaPrimaria, tasaIvaExtra: l.tasaIvaExtra };
    neto += lt;
    bases.push(base);

    if (l.bruto > 0 && tasaTotal > 0) {
      hayBruto = true;
      esperado += brutoEsperadoLinea(l.bruto, l.descuentoPct, l.cantidad);
      tolerancia += toleranciaLinea(l.cantidad, tasaTotal, decimales);
      if (!l.guiadaPorBruto) {
        todasGuiadas = false;
      }
    } else {
      // Línea sin precio público (exenta o sin precio): cuenta por lo que realmente suma.
      esperado += lt;
      basesSinBruto.push(base);
    }
  });

  const impuestos = impuestosDocumento(bases);
  if (basesSinBruto.length > 0) {
    esperado += impuestosDocumento(basesSinBruto).total;
    // Sus impuestos se redondean junto con los del resto del documento
    tolerancia += 1;
  }

  const totalCalculado = neto + impuestos.total;
  if (hayBruto && !todasGuiadas) {
    tolerancia = Math.min(tolerancia, TOPE_REDONDEO_INFERIDO);
  }
  const diff = esperado - totalCalculado;
  const aplica = hayBruto && diff !== 0 && Math.abs(diff) <= tolerancia;

  return {
    neto,
    impuestoPrimario: impuestos.primario,
    ivaExtra: impuestos.ivaExtra,
    totalCalculado,
    totalBrutoEsperado: esperado,
    tolerancia,
    aplicaRedondeo: aplica,
    redondeo: aplica ? diff : 0,
    total: aplica ? esperado : totalCalculado
  };
}
